/**
 * Centralized route definitions for the application
 * This file serves as a single source of truth for route configurations
 * to prevent duplication and maintain consistency across the codebase.
 */

/**
 * Public routes that don't require authentication
 */
export const PUBLIC_ROUTES = [
  "/login",
  "/forgot-password",
  "/login-otp",
  "/protected/reset-password",
  "/auth/callback",
  "/bookings", // Public booking flow
  "/review", // Public customer review form (QR landing, no auth)
  "/qr", // Standalone review-QR display/print page (no auth)
] as const;

/**
 * Portal routes that have their own authentication handling
 */
export const PORTAL_ROUTES = [
  "/superadmin",
  "/booking",
  "/barstaff",
  "/customerportal",
] as const;

/**
 * Routes that should render with minimal layout (no header/footer)
 */
export const MINIMAL_LAYOUT_ROUTES = [
  "/protected/reset-password",
  "/auth/callback",
  "/login",
  "/login-otp",
  "/forgot-password",
  "/venues", // Terms pages should have minimal layout
] as const;

/**
 * Helper functions for route checking
 */
export const routeUtils = {
  /**
   * Check if a pathname is a public route
   */
  isPublicRoute: (pathname: string): boolean => {
    // Check exact matches
    if (PUBLIC_ROUTES.includes(pathname as any)) {
      return true;
    }

    // Check if path starts with any public route prefix
    // This handles routes like /venues/[id]/terms, /bookings/summary, etc.
    const publicPrefixes = ['/venues/', '/bookings/', '/review/', '/qr/'];
    return publicPrefixes.some(prefix => pathname.startsWith(prefix));
  },

  /**
   * Check if a pathname starts with any portal route
   */
  isPortalPage: (pathname: string): boolean => {
    return PORTAL_ROUTES.some((portal) => pathname.startsWith(portal));
  },

  /**
   * Check if a pathname requires minimal layout
   */
  isMinimalLayout: (pathname: string): boolean => {
    // Check exact matches
    if (MINIMAL_LAYOUT_ROUTES.includes(pathname as any)) {
      return true;
    }

    // Check if path starts with /venues/ for terms pages, or /review/ and /qr/
    // for the standalone QR review form and its QR display/print page
    return (
      pathname.startsWith('/venues/') ||
      pathname.startsWith('/review/') ||
      pathname.startsWith('/qr/')
    );
  },
};

/**
 * Type definitions for route arrays
 */
export type PublicRoute = (typeof PUBLIC_ROUTES)[number];
export type PortalRoute = (typeof PORTAL_ROUTES)[number];
export type MinimalLayoutRoute = (typeof MINIMAL_LAYOUT_ROUTES)[number];
